import { StatusProxy } from "@focus/focusUI";
import { useCallback, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  ContactsByOperatorButton,
  type ContactsByOperatorSelection,
} from "../components/ContactsByOperatorButton";
import { ConjunctionContactsPanel } from "../components/conjunctions/ConjunctionContactsPanel";
import { InfiniteTable } from "../components/table/InfiniteTable";
import { TABLE_ACTION } from "../components/table/TableActionColumn";
import { TableShell } from "../components/table/TableShell";
import {
  conjunctionColumnsLayout,
  useAvailableconjunctionHeaders,
  useSavedConjunctionColumns,
} from "../services/configuration";
import {
  conjunctions,
  getConjunctionRows,
  getConjunctionsPage,
} from "../services/conjunctions";
import { CONJ_DEFAULT_VISIBLE_COLUMNS } from "../services/conjunctions/Headers";
import type { ColumnFiltersState } from "../services/helpers/pager";
import { downloadFileZip } from "../services/downloaduploadOrbits/actions";
import {
  getColumnDefinitions,
  type ServerColumnConfig,
} from "../services/helpers/headers";
import type { RootState } from "../store";
import { RiskLevel } from "../types/conjunctions";
import { combineErrors, combineStatuses } from "../utils";

export const ConjunctionsContainer: React.FC = () => {
  const dispatch = useDispatch();
  const [contactsSelection, setContactsSelection] =
    useState<ContactsByOperatorSelection | null>(null);

  const conjunctionsActions = conjunctions.actions;
  const conjunctionsLayoutActions = conjunctionColumnsLayout.actions;

  // Request the user active columns to the server's DB
  const availableHeaders = useAvailableconjunctionHeaders();
  const savedColumnsLayout = useSavedConjunctionColumns();
  const columnDefinitions = useMemo(
    () => getColumnDefinitions(availableHeaders.value),
    [availableHeaders.value],
  );

  const saveColumnLayout = (serverConfig: ServerColumnConfig) => {
    dispatch(conjunctionsLayoutActions.saveItems(serverConfig));
  };

  const conjunctionsFilter = useSelector(
    (state: RootState) => state.conjunctions.filter,
    shallowEqual,
  );

  const handleConjunctionsFilterChange = useCallback(
    (filtered: ColumnFiltersState) =>
      dispatch(conjunctionsActions.setFilter(filtered)),
    [conjunctionsActions, dispatch],
  );

  const handleContactsSelection = useCallback(
    (selection: ContactsByOperatorSelection) => {
      setContactsSelection(selection);
    },
    [],
  );

  const combinedStatus = combineStatuses([
    availableHeaders.status,
    savedColumnsLayout.status,
  ]);
  const combinedError = combineErrors([
    availableHeaders.error,
    savedColumnsLayout.error,
  ]);

  return (
    <div className="foc-content flex flex-col overflow-hidden">

      <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50/40 p-5">
        <StatusProxy status={combinedStatus} error={combinedError}>
          <div className="h-full min-h-0">
            <TableShell
              className="conjunctions-table-shell"
              contentClassName="flex h-full min-h-0 min-w-0 overflow-hidden"
            >
              {contactsSelection && (
                <ConjunctionContactsPanel
                  onClose={() => setContactsSelection(null)}
                  selection={contactsSelection}
                />
              )}

              <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
                <InfiniteTable
                  actions={[
                    TABLE_ACTION.DOWNLOAD,
                    TABLE_ACTION.INFO,
                    TABLE_ACTION.EVENT,
                    TABLE_ACTION.PLOTS,
                    (data) => (
                      <ContactsByOperatorButton
                        data={data}
                        onSelect={handleContactsSelection}
                      />
                    ),
                  ]}
                  base="conjunctions"
                  columnDefinitions={columnDefinitions}
                  controlledColumnFilter={conjunctionsFilter}
                  controlledColumnFilterChange={handleConjunctionsFilterChange}
                  defaultVisibleValues={CONJ_DEFAULT_VISIBLE_COLUMNS}
                  downloadData={(payload) => dispatch(downloadFileZip(payload))}
                  getNumberOfRows={getConjunctionRows}
                  rowClassRules={{
                    "!bg-red-50": (params) =>
                      params.original.riskLevel.value === RiskLevel.ALERT,
                    "!bg-amber-50": (params) =>
                      params.original.riskLevel.value === RiskLevel.WARNING,
                  }}
                  rowsGetter={getConjunctionsPage}
                  saveColumnLayout={saveColumnLayout}
                  serverDisplayConfig={savedColumnsLayout.items}
                />
              </div>
            </TableShell>
          </div>
        </StatusProxy>
      </main>
    </div>
  );
};
